/**
 * TOSS Web Application - Permission Store
 *
 * Zustand store for the granular permission-keys RBAC model. The store holds a
 * `Set<string>` of keys returned by `GET /api/lk/permissions/my-keys` and is the
 * single source of truth for any client-side permission check.
 *
 * Hooks for components: `usePermission(key)` and `useAnyPermission(...keys)`.
 * Outside React: `hasPermissionKey(key)` for a non-reactive snapshot.
 */

import { create } from 'zustand';
import apiClient, { getErrorMessage, TokenStorage } from '@/api/client';

// ============================================================================
// Types
// ============================================================================

interface MyKeysResponse {
  role: string;
  keys: string[];
}

interface PermissionState {
  /** Granular permission keys for the current user (`null` until first load). */
  keys: Set<string> | null;
  /** A fetch is in flight. Used by route guards to hold the route during refresh. */
  isLoading: boolean;
  /** Permissions have been loaded at least once for the current session. */
  isLoaded: boolean;
  /** Last load error, if any. */
  error: string | null;
}

interface PermissionActions {
  /** Fetch the current user's permission keys from the API. */
  fetchMyKeys: () => Promise<void>;
  /** Reactive check — true if `key` is in the current user's granted set. */
  has: (key: string) => boolean;
  /** Clear permissions (called on logout). */
  reset: () => void;
  /**
   * Re-fetch without nulling out `keys` first. Atomic swap on success keeps
   * route guards stable across the refresh (see PermissionRoute).
   */
  refreshPermissions: () => Promise<void>;
}

type PermissionStore = PermissionState & PermissionActions;

// ============================================================================
// Initial State
// ============================================================================

const initialState: PermissionState = {
  keys: null,
  isLoading: false,
  isLoaded: false,
  error: null,
};

// ============================================================================
// Store
// ============================================================================

export const usePermissionStore = create<PermissionStore>()((set, get) => ({
  ...initialState,

  fetchMyKeys: async () => {
    if (get().isLoading) return;

    // The client audience never enters the permission matrix (decision Р3 — every
    // /api/client/* route is [RequirePermission]-free, gated by role/ClientAccess
    // instead). Resolve straight to an empty grant set rather than calling an
    // endpoint this audience has no access to; has() then correctly returns false
    // for everything without a network round trip.
    if (TokenStorage.getAudience() === 'client') {
      set({ keys: new Set<string>(), isLoaded: true, isLoading: false, error: null });
      return;
    }

    set({ isLoading: true, error: null });

    try {
      const response = await apiClient.get<MyKeysResponse>('/api/lk/permissions/my-keys');
      const keys = new Set<string>(response.data.keys);

      set({ keys, isLoaded: true, isLoading: false, error: null });
      console.debug('[PermissionStore] Keys loaded:', keys.size, 'permissions');
    } catch (error) {
      console.error('[PermissionStore] Failed to load permission keys:', error);
      set({
        isLoading: false,
        error: getErrorMessage(error),
      });
    }
  },

  has: (key: string): boolean => {
    const { keys } = get();
    if (!keys) return false;
    return keys.has(key);
  },

  reset: () => {
    set(initialState);
    console.debug('[PermissionStore] Permissions cleared');
  },

  refreshPermissions: async () => {
    // Don't null out keys here. fetchMyKeys() guards against re-entrancy via
    // isLoading and atomically swaps the new key set in on success — leaving keys
    // populated until then prevents PermissionRoute from briefly seeing null keys
    // and redirecting to /403 (e.g. right after the matrix is saved).
    if (get().isLoading) return;
    await get().fetchMyKeys();
  },
}));

// ============================================================================
// Selectors
// ============================================================================

export const selectIsPermissionsLoaded = (state: PermissionStore) => state.isLoaded;
export const selectIsPermissionsLoading = (state: PermissionStore) => state.isLoading;
export const selectPermissionError = (state: PermissionStore) => state.error;

// ============================================================================
// Utility Functions (for use outside React components)
// ============================================================================

/**
 * Non-reactive snapshot check — use in plain functions, not in JSX.
 * Inside components, prefer `usePermission(key)`.
 */
export function hasPermissionKey(key: string): boolean {
  return usePermissionStore.getState().has(key);
}

export default usePermissionStore;
